import { T, formatCents, formatMoney, lineTotalCents, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import {
  Banknote,
  CalendarClock,
  CheckCheck,
  CreditCard,
  Loader2,
  Lock,
  Minus,
  Package,
  Percent,
  Pizza,
  Plus,
  Soup,
  StickyNote,
  Tag,
  X,
} from 'lucide-react'

import { noTimeLeftMessage } from './preorder'

import type { OrderCartLine } from './cart'
import type { CartTotals, DiscountMode } from './cart-math'

/**
 * The cart panel, ported from legacy `components/order-builder/*`.
 *
 * Six files there, one here: the items, the discount, the coupon, the payment
 * method, the note and the totals were separate components that were only ever
 * rendered together, in this order, by two callers. They are still separate
 * functions; they are no longer separate modules to keep in step.
 */

export const PAYMENT_METHODS = [
  { value: 'cash', label: tt('Hotovosť'), icon: Banknote },
  { value: 'card_courier', label: tt('Karta kuriér'), icon: CreditCard },
  { value: 'card_terminal', label: tt('Karta terminál'), icon: CreditCard },
] as const

const FIELD_CLASSES =
  'px-2.5 py-1.5 rounded-xl bg-background border border-border text-xs outline-none focus:ring-2 focus:ring-primary/20'

export interface CouponState {
  loading?: boolean
  error?: string
  discountCents?: number
  code?: string
}

export function CartItemsList({
  cart,
  onQuantityChange,
  onRemoveLine,
  onRemoveBoxGroup,
  onEditExtras,
  emptyText,
  unlockAll = false,
}: {
  cart: OrderCartLine[]
  onQuantityChange: (lineIndex: number, delta: number) => void
  onRemoveLine: (lineIndex: number) => void
  onRemoveBoxGroup: (boxGroupId: string) => void
  onEditExtras?: (lineIndex: number) => void
  emptyText?: string
  /** Editing an earlier round: its lines become writable, still badged. */
  unlockAll?: boolean
}) {
  if (cart.length === 0) {
    return (
      <div className="text-center text-muted-foreground text-sm py-8 whitespace-pre-line">
        {emptyText ?? tt('Kliknite na produkt\npre pridanie do košíka')}
      </div>
    )
  }

  const drawnBoxes = new Set<string>()
  // §9.178 — the lunch box is drawn once, at the first lunch line. A Set like `drawnBoxes` above
  // rather than a boolean: the lint rule refuses reassigning a local after render, and mutating one
  // is what the existing box grouping already does for the same reason.
  const drawnLunch = new Set<string>()
  const hasLocked = cart.some((line) => line.locked)
  const hasUnlocked = cart.some((line) => !line.locked)

  return (
    // The list of lines had no addressable root, so a test could reach the total but never *what the
    // lines say* — which is where §9.118's bundle price shows up.
    <div className="space-y-2" data-order-cart>
      {hasLocked && !unlockAll && (
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1 flex items-center gap-1.5 pt-1">
          <Lock className="w-3 h-3" />{' '}
          {hasUnlocked ? <T>1. kolo (uzamknuté)</T> : <T>Predošlé kolo</T>}
        </p>
      )}
      {hasLocked && unlockAll && (
        <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 px-1 flex items-center gap-1.5 pt-1">
          <T>⚠ Editujete predošlé kolo - zmeny prepíšu pôvodnú objednávku</T>
        </p>
      )}

      {cart.map((line, lineIndex) => {
        // §9.178 — the day's menu, drawn as one box. Grouped at render over a client-only flag
        // rather than by giving these lines a `boxGroupId`: that field is stored and means *remove
        // as a unit*, and each meal here was added on its own.
        if (line.lunchGroup === true) {
          if (drawnLunch.has('lunch')) return null
          drawnLunch.add('lunch')
          const meals = cart.filter((candidate) => candidate.lunchGroup === true)
          const lunchTotal = meals.reduce(
            (total, meal) => total + lineTotalCents(meal.unitPrice, meal.quantity),
            0,
          )
          return (
            <div
              key="lunch-group"
              data-cart-lunch-box
              className="rounded-xl border border-border bg-muted/50 overflow-hidden"
            >
              <div className="flex items-center gap-2 px-3 py-2 bg-muted border-b border-border">
                <Soup className="w-3.5 h-3.5 text-foreground flex-shrink-0" />
                <span className="text-xs font-black text-foreground flex-1">
                  <T>Denné menu</T>
                </span>
                <span className="text-xs font-black text-muted-foreground">
                  {formatCents(lunchTotal)}
                </span>
                {/* §9.185 - one bin for the set, at the owner's decision. A *denné menu* is a price
                    for a soup and a main together, and `repriceLunchSoups` charges the soup its
                    bundle price only while a main is beside it: removing one half silently reprices
                    the other. */}
                <button
                  type="button"
                  data-lunch-remove
                  aria-label={tt('Odobrať denné menu')}
                  onClick={() => {
                    // Back to front: each removal reindexes the ones after it.
                    for (let index = cart.length - 1; index >= 0; index -= 1) {
                      if (cart[index]?.lunchGroup === true) onRemoveLine(index)
                    }
                  }}
                  className="w-5 h-5 rounded-md bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/40 ml-1 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="divide-y divide-border/40">
                {meals.map((meal) => (
                  <div
                    key={`lunch-${meal.productId}-${meal.unitPrice}`}
                    className="flex items-center gap-2 px-3 py-1.5"
                    data-cart-lunch-line
                  >
                    <p className="text-xs font-semibold text-foreground flex-1 truncate">
                      {meal.quantity > 1 && `${meal.quantity}× `}
                      <T>{meal.productName}</T>
                    </p>
                    <p className="text-[10px] text-muted-foreground shrink-0">
                      {formatCents(lineTotalCents(meal.unitPrice, meal.quantity))}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )
        }
        if (line.boxGroupId) {
          if (drawnBoxes.has(line.boxGroupId)) return null
          drawnBoxes.add(line.boxGroupId)
          const slots = cart.filter((candidate) => candidate.boxGroupId === line.boxGroupId)
          const boxTotal = slots.reduce(
            (total, slot) => total + lineTotalCents(slot.unitPrice, slot.quantity),
            0,
          )
          return (
            <div
              key={line.boxGroupId}
              className="rounded-xl border border-border bg-muted/50 overflow-hidden"
            >
              <div className="flex items-center gap-2 px-3 py-2 bg-muted border-b border-border">
                <Package className="w-3.5 h-3.5 text-foreground flex-shrink-0" />
                <span className="text-xs font-black text-foreground flex-1">
                  {line.boxGroupName ?? 'Burger Box'}
                </span>
                <span className="text-xs font-black text-muted-foreground">
                  {formatCents(boxTotal)}
                </span>
                <button
                  type="button"
                  aria-label={tt('Odstrániť box')}
                  onClick={() => onRemoveBoxGroup(line.boxGroupId ?? '')}
                  className="w-5 h-5 rounded-md bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/40 ml-1 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="divide-y divide-border/40">
                {slots.map((slot) => (
                  <div key={`${slot.productId}-${slot.boxSlotLabel}`} className="flex items-center gap-2 px-3 py-1.5">
                    {slot.boxSlotLabel && (
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide w-12 shrink-0">
                        {slot.boxSlotLabel}
                      </span>
                    )}
                    <p className="text-xs font-semibold text-foreground flex-1 truncate">
                      <T>{slot.productName}</T>
                    </p>
                    <p className="text-[10px] text-muted-foreground shrink-0">
                      {formatMoney(slot.unitPrice)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )
        }

        const readOnly = line.locked === true && !unlockAll
        return (
          <div
            key={`${line.productId}-${lineIndex}`}
            className={cn(
              'bg-muted/50 rounded-xl p-3 flex items-center gap-2',
              readOnly && 'opacity-60',
            )}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {line.locked && (
                  <Lock className="w-3 h-3 text-muted-foreground shrink-0" aria-hidden="true" />
                )}
                <p className="text-sm font-semibold text-foreground truncate">
                  <T>{line.productName}</T>
                </p>
              </div>
              {line.pizzaExtras && (
                <p className="text-[10px] text-primary truncate">+ {line.pizzaExtras}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {formatMoney(line.unitPrice)} ×{line.quantity} ={' '}
                <span className="font-semibold text-foreground">
                  {formatCents(lineTotalCents(line.unitPrice, line.quantity))}
                </span>
              </p>
            </div>

            {line.isPizza && onEditExtras && !readOnly && (
              <button
                type="button"
                aria-label={tt('Upraviť prísady')}
                onClick={() => onEditExtras(lineIndex)}
                className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors"
              >
                <Pizza className="w-3.5 h-3.5" />
              </button>
            )}

            {!readOnly && (
              <>
                <button
                  type="button"
                  aria-label={tt('Menej')}
                  onClick={() => onQuantityChange(lineIndex, -1)}
                  className="w-6 h-6 rounded-lg bg-background border border-border flex items-center justify-center hover:bg-muted transition-colors"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className="w-5 text-center text-sm font-bold tabular-nums">
                  {line.quantity}
                </span>
                <button
                  type="button"
                  aria-label={tt('Viac')}
                  onClick={() => onQuantityChange(lineIndex, 1)}
                  className="w-6 h-6 rounded-lg bg-background border border-border flex items-center justify-center hover:bg-muted transition-colors"
                >
                  <Plus className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  aria-label={tt('Odstrániť')}
                  onClick={() => onRemoveLine(lineIndex)}
                  className="w-6 h-6 rounded-lg bg-red-500/10 text-red-500 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function CartDiscountSection({
  discountMode,
  onDiscountModeChange,
  discountValue,
  onDiscountValueChange,
  couponCode,
  onCouponCodeChange,
  coupon,
  onClearCoupon,
  onApplyCoupon,
}: {
  discountMode: DiscountMode
  onDiscountModeChange: (mode: DiscountMode) => void
  discountValue: string
  onDiscountValueChange: (value: string) => void
  couponCode: string
  onCouponCodeChange: (code: string) => void
  coupon: CouponState | null
  onClearCoupon: () => void
  onApplyCoupon: () => void
}) {
  const applied = coupon?.discountCents !== undefined

  return (
    <div>
      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
        <T>Zľava</T>
      </p>
      <div className="flex gap-1.5 mb-1.5">
        <div className="flex rounded-xl border border-border overflow-hidden shrink-0">
          {(['percent', 'fixed'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => onDiscountModeChange(mode)}
              className={cn(
                'px-2.5 py-1.5 text-xs font-bold transition-all',
                mode === 'fixed' && 'border-l border-border',
                discountMode === mode
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {mode === 'percent' ? '%' : '€'}
            </button>
          ))}
        </div>
        {/* §9.267 - `'%'` repeated the button lit immediately to the left, and `'0.00'` put a
          * decimal **point** on a Slovak screen, which is what N-02 exists to stop everywhere else.
          * Both say what to type now, and say it as a hint rather than as a value. */}
        <input
          type="number"
          min={0}
          max={discountMode === 'percent' ? 100 : undefined}
          step={discountMode === 'percent' ? 1 : 0.1}
          value={discountValue}
          onChange={(event) => onDiscountValueChange(event.target.value)}
          placeholder={discountMode === 'percent' ? tt('napr. 10') : tt('napr. 5,00')}
          aria-label={tt('Zľava')}
          className={cn(FIELD_CLASSES, 'flex-1')}
        />
        {discountValue && (
          <button
            type="button"
            aria-label={tt('Zrušiť zľavu')}
            onClick={() => onDiscountValueChange('')}
            className="px-1.5 text-muted-foreground hover:text-red-500 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {applied ? (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-2.5 py-1.5">
          <CheckCheck className="w-3 h-3 text-emerald-500" />
          <span className="text-xs text-emerald-600 flex-1 font-semibold">
            {coupon?.code} −{formatCents(coupon?.discountCents ?? 0)}
          </span>
          <button
            type="button"
            aria-label={tt('Zrušiť kupón')}
            onClick={onClearCoupon}
            className="text-emerald-500 hover:text-emerald-700"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="flex gap-1.5">
          <input
            value={couponCode}
            onChange={(event) => onCouponCodeChange(event.target.value.toUpperCase())}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onApplyCoupon()
            }}
            placeholder={tt('Kód kupóna')}
            aria-label={tt('Kód kupóna')}
            className={cn(FIELD_CLASSES, 'flex-1 uppercase placeholder:normal-case')}
          />
          <button
            type="button"
            onClick={onApplyCoupon}
            disabled={!couponCode.trim()}
            className={cn(FIELD_CLASSES, 'font-bold disabled:opacity-40 hover:text-primary')}
          >
            {coupon?.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'OK'}
          </button>
        </div>
      )}
      {coupon?.error && <p className="text-[10px] text-red-500 mt-1">{coupon.error}</p>}
    </div>
  )
}

export function CartPaymentMethods({
  value,
  onChange,
  label,
  exclude,
}: {
  value: string
  onChange: (value: string) => void
  label?: string
  /** The restaurant till hides `card_courier` — there is no courier at a table. */
  exclude?: readonly string[]
}) {
  const methods = exclude?.length
    ? PAYMENT_METHODS.filter((method) => !exclude.includes(method.value))
    : PAYMENT_METHODS

  return (
    <div className="space-y-2">
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
        {label ?? tt('Platba')}
      </p>
      <div className="grid grid-cols-1 gap-1.5">
        {methods.map((method) => (
          <button
            key={method.value}
            type="button"
            onClick={() => onChange(method.value)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all border',
              value === method.value
                ? 'bg-primary/10 border-primary text-primary'
                : 'bg-background border-border text-muted-foreground hover:text-foreground',
            )}
          >
            <method.icon className="w-4 h-4" />
            {method.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export function CartNotes({
  value,
  onChange,
  placeholder,
  rows = 2,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        <StickyNote className="w-3 h-3" /> <T>Poznámka</T>
      </p>
      {/* §9.267 - was *Poznámky **ku** objednávke*. `ku` is for words beginning with k or g; the
        * same construction is spelled correctly two screens away, in the revenue handover. */}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder ?? tt('Poznámky k objednávke...')}
        aria-label={tt('Poznámka')}
        rows={rows}
        className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 resize-none"
      />
    </div>
  )
}

/**
 * *Časenka* — the order is for later. Ported from legacy `components/order-builder/casenka-toggle.tsx`.
 *
 * **This was a missed port, not a new feature.** Legacy's `new-order.tsx:1885` renders this component
 * and the AGOS builder had nothing — see FB-193. So the strings, the toggle, the two labelled inputs,
 * the echoed label and the two notes are legacy's, per §8.
 *
 * One deliberate divergence, recorded: the conversion is `zonedWallTimeToUtcMs`, not legacy's
 * `bratislavaDateTimeMs`. Legacy samples the zone offset at the naive time *treated as UTC* and adds it
 * once, which is FB-C09 — on the morning the clocks go forward it lands an hour early, on the previous
 * day. Two passes fix it and every other instant is identical.
 */
export function CartPreorder({
  enabled,
  onEnabledChange,
  date,
  time,
  onDateChange,
  onTimeChange,
  minDate,
  minTime,
  noTimeLeft,
  scheduledLabel,
}: {
  enabled: boolean
  onEnabledChange: (value: boolean) => void
  date: string
  time: string
  onDateChange: (value: string) => void
  onTimeChange: (value: string) => void
  minDate: string
  minTime: string
  /** Whether the ten-minute rule has run today out of bookable times — FB-193. */
  noTimeLeft: boolean
  /** The instant echoed back, or `null` while either piece is missing. */
  scheduledLabel: string | null
}) {
  return (
    <div
      data-preorder
      className={cn(
        'rounded-xl border-2 transition-all overflow-hidden',
        enabled ? 'border-primary bg-primary/5' : 'border-border bg-background',
      )}
    >
      <button
        type="button"
        data-preorder-toggle
        aria-pressed={enabled}
        onClick={() => onEnabledChange(!enabled)}
        className="w-full flex items-center gap-3 px-3 py-2.5"
      >
        <div
          className={cn(
            'w-8 h-4 rounded-full transition-all relative flex-shrink-0',
            enabled ? 'bg-primary' : 'bg-muted',
          )}
        >
          <div
            className={cn(
              'absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all',
              enabled ? 'left-4' : 'left-0.5',
            )}
          />
        </div>
        <CalendarClock
          className={cn('w-4 h-4 flex-shrink-0', enabled ? 'text-primary' : 'text-muted-foreground')}
        />
        <div className="text-left">
          <span
            className={cn(
              'text-sm font-bold block',
              enabled ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            <T>Časenka</T>
          </span>
          <span className="text-xs text-muted-foreground">
            <T>Naplánovaný čas doručenia</T>
          </span>
        </div>
      </button>

      {enabled && (
        <div className="px-3 pb-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                <T>Dátum</T>
              </p>
              <input
                type="date"
                value={date}
                min={minDate}
                onChange={(event) => onDateChange(event.target.value)}
                aria-label={tt('Dátum')}
                data-preorder-date
                className="w-full px-2 py-2 rounded-lg bg-background border border-border text-xs outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                <T>Čas</T>
              </p>
              <input
                type="time"
                value={time}
                min={minTime}
                onChange={(event) => onTimeChange(event.target.value)}
                aria-label={tt('Čas')}
                data-preorder-time
                className="w-full px-2 py-2 rounded-lg bg-background border border-border text-xs outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          {noTimeLeft ? (
            // A `min` no time can satisfy would otherwise read as a broken field: the picker refuses
            // every value and says nothing. This is the last ten minutes of the day, when a časenka
            // ten minutes out is already tomorrow.
            <p className="text-[10px] text-amber-500" data-preorder-note data-no-time-left>
              {noTimeLeftMessage()}
            </p>
          ) : scheduledLabel === null ? (
            <p className="text-[10px] text-amber-500" data-preorder-note>
              <T>Zadajte dátum a čas doručenia</T>
            </p>
          ) : (
            <div
              data-preorder-note
              className="flex items-center gap-1.5 bg-primary/10 rounded-lg px-2.5 py-2 text-xs text-primary font-semibold"
            >
              <CalendarClock className="w-3 h-3" /> {scheduledLabel}
            </div>
          )}
          {/* Legacy's own note, and the only place either app states the 60-minute rule the kitchen
            * board works by (`KitchenPage`'s `isPreorderDueNow`). Dropping it would leave the till
            * unable to answer *when does this reach the kitchen*. */}
          <p className="text-[10px] text-muted-foreground italic">
            <T>Objednávka sa zobrazí na nástenke ihneď. Do kuchyne príde 60 min pred doručením.</T>
          </p>
        </div>
      )}
    </div>
  )
}

export function CartTotalsPanel({
  totals,
  showDelivery,
  discountLabel,
  couponCode,
}: {
  totals: CartTotals
  showDelivery: boolean
  discountLabel?: string
  couponCode?: string | null
}) {
  return (
    <div className="bg-muted/50 rounded-xl p-3 space-y-1.5 text-sm">
      <div className="flex justify-between text-muted-foreground">
        <span>
          <T>Medzisúčet</T>
        </span>
        <span>{formatCents(totals.subtotalCents)}</span>
      </div>
      {showDelivery && (
        <div className="flex justify-between text-muted-foreground">
          <span>
            <T>Dovoz</T>
          </span>
          <span data-cart-delivery>{formatCents(totals.deliveryCents)}</span>
        </div>
      )}
      {/* §9.182 - its own row, kept out of the subtotal: a deposit is money handed over and given
          back, not something bought. The server charges it either way; this screen used to omit it
          and quote a price the till then exceeded. */}
      {totals.depositCents > 0 && (
        <div className="flex justify-between text-muted-foreground">
          <span>
            <T>Zálohy (vratné)</T>
          </span>
          <span data-cart-deposits>{formatCents(totals.depositCents)}</span>
        </div>
      )}
      {totals.manualDiscountCents > 0 && (
        <div className="flex justify-between text-emerald-600 font-semibold">
          <span className="flex items-center gap-1">
            <Percent className="w-3 h-3" /> {discountLabel ?? tt('Zľava')}
          </span>
          <span>−{formatCents(totals.manualDiscountCents)}</span>
        </div>
      )}
      {totals.couponDiscountCents > 0 && (
        <div className="flex justify-between text-emerald-600 font-semibold">
          <span className="flex items-center gap-1">
            <Tag className="w-3 h-3" /> {couponCode ?? tt('Kupón')}
          </span>
          <span>−{formatCents(totals.couponDiscountCents)}</span>
        </div>
      )}
      <div className="flex justify-between font-bold text-foreground text-base pt-1 border-t border-border">
        <span>
          <T>Celkom</T>
        </span>
        <span className="text-primary" data-cart-total>
          {formatCents(totals.totalCents)}
        </span>
      </div>
    </div>
  )
}
